export const LYRA_REPLAY_PROVIDER_DATA_KEY = 'lyraReplay';

export type LyraReplayPayload = {
  provider: string;
  blocks: Array<Record<string, unknown>>;
  kind?: string;
  [key: string]: unknown;
};

export function createLyraReplayPayload(args: {
  provider: string;
  blocks: Array<Record<string, unknown>>;
  kind?: string;
  [key: string]: unknown;
}): LyraReplayPayload {
  const { provider, blocks, kind, ...rest } = args;
  return {
    provider,
    blocks,
    ...(kind ? { kind } : {}),
    ...rest,
  };
}

export function getLyraReplayPayload(
  providerData: Record<string, unknown> | undefined,
  provider?: string,
): LyraReplayPayload | undefined {
  const replay = providerData?.[LYRA_REPLAY_PROVIDER_DATA_KEY];
  if (!isRecord(replay)) {
    return undefined;
  }
  if (provider && replay.provider !== provider) {
    return undefined;
  }
  if (
    typeof replay.provider !== 'string' ||
    !Array.isArray(replay.blocks) ||
    !replay.blocks.every(isRecord)
  ) {
    return undefined;
  }
  return replay as LyraReplayPayload;
}

export function withLyraReplayPayload(
  providerData: Record<string, unknown> | undefined,
  replay: LyraReplayPayload,
): Record<string, unknown> {
  return {
    ...(providerData ?? {}),
    [LYRA_REPLAY_PROVIDER_DATA_KEY]: replay,
  };
}

export function sanitizeLyraReplayProviderData<T>(value: T): T {
  return sanitize(value) as T;
}

function sanitize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitize);
  }
  if (!isRecord(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (key === 'encrypted_content') {
      result[key] = '[redacted encrypted_content]';
      continue;
    }
    if (key === 'data' && value.type === 'redacted_thinking') {
      result[key] = '[redacted redacted_thinking data]';
      continue;
    }
    if (key === LYRA_REPLAY_PROVIDER_DATA_KEY && isRecord(entry)) {
      const blocks = Array.isArray(entry.blocks) ? entry.blocks : [];
      result[key] = {
        provider: entry.provider,
        kind: entry.kind,
        blockCount: blocks.length,
        hasEncryptedContent: blocks.some(
          (block) =>
            isRecord(block) && typeof block.encrypted_content === 'string',
        ),
        hasRedactedThinking: blocks.some(
          (block) => isRecord(block) && block.type === 'redacted_thinking',
        ),
      };
      continue;
    }
    result[key] = sanitize(entry);
  }
  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
